/**
 * TreeListView — the list tab as a *builder*, not just a viewer.
 *
 * Acts as the orchestration layer: builds adjacency maps, handles
 * search / expansion / cycles / jump-to-node / orphan detection, and
 * delegates the actual visual rendering to <StyleNestedCards /> under
 * ./styles/.
 *
 * Wiring inside <IvrTreeDetail />:
 *   - Data (nodes, edges, tree) comes from the page's TanStack queries
 *     so we don't duplicate fetches here.
 *   - Clicking a node name calls `onEditNode(node)` so the page can
 *     open its shared <NodeEditorDrawer />.
 *   - After any mutation we rely on the caller's TanStack
 *     `invalidateQueries` to refetch (AddChildPopover handles its own
 *     invalidation via queryClient).
 *
 * Behaviour:
 *   - Starts with *all* nodes expanded on first mount — then respects
 *     user collapses/expands.
 *   - Cycle detection: visited-set per branch; on cycle, emits an
 *     "↩ back to IVR-XXXX" marker instead of recursing.
 *   - Orphans (nodes not reachable from root) are surfaced in their own
 *     block at the bottom, each with its own [+] so the user can wire
 *     them up.
 *   - Search: matches on ref / name / type; expands ancestors of hits.
 *   - Breadcrumb: shows "Parent IVR: XXXX", clickable -> onEditNode.
 *
 * Tests live in TreeListView.test.tsx. Keep the public API stable!
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import type { IvrNodeRow } from '@/api/ivrNodes';
import type { IvrEdgeRow } from '@/api/ivrEdges';
import { AddChildPopover } from './AddChildPopover';
import { cn } from '@/lib/cn';
import { formatRef } from '@/lib/formatRef';
import {
  ChevronDown, PlusCircle, MinusCircle, Search, Network,
} from 'lucide-react';
import { StyleNestedCards } from './styles/StyleNestedCards';

// ── Public props ──────────────────────────────────────────────────────────────

export interface TreeListViewProps {
  treeId: string;
  nodes: ReadonlyArray<IvrNodeRow>;
  edges: ReadonlyArray<IvrEdgeRow>;
  rootNodeId: string | null | undefined;
  canMutate: boolean;
  /** Open the <NodeEditorDrawer /> for this node. */
  onEditNode: (node: IvrNodeRow) => void;
  /** Open a confirmation dialog to delete this node. */
  onDeleteNode?: (node: IvrNodeRow) => void;
}

// ── Types ─────────────────────────────────────────────────────────────────────

interface AdjacencyMap { [fromNodeId: string]: IvrEdgeRow[] }

// ── Map builders ──────────────────────────────────────────────────────────────

/** Numeric DTMF order: 1–9, 0, *, #; non-DTMF keys (next, fallback, timeout…) sort after. */
const DTMF_ORDER = ['1','2','3','4','5','6','7','8','9','0','*','#'];

function buildAdjacency(edges: ReadonlyArray<IvrEdgeRow>): AdjacencyMap {
  const adj: AdjacencyMap = {};
  for (const e of edges) {
    if (!e.to_node_id) continue;
    (adj[e.from_node_id] ??= []).push(e);
  }
  for (const key of Object.keys(adj)) {
    adj[key]!.sort((a, b) => {
      const ai = DTMF_ORDER.indexOf(a.branch_key);
      const bi = DTMF_ORDER.indexOf(b.branch_key);
      // Both DTMF keys → sort by DTMF position (1,2,3…9,0,*,#).
      if (ai !== -1 && bi !== -1) return ai - bi;
      // One is DTMF, one is not → DTMF comes first.
      if (ai !== -1) return -1;
      if (bi !== -1) return 1;
      // Neither is DTMF → fall back to sort_order.
      return a.sort_order - b.sort_order;
    });
  }
  return adj;
}

function buildNodeMap(nodes: ReadonlyArray<IvrNodeRow>): Record<string, IvrNodeRow> {
  const map: Record<string, IvrNodeRow> = {};
  for (const n of nodes) map[n.id] = n;
  return map;
}

function collectReachable(
  rootId: string | null | undefined,
  adj: AdjacencyMap,
): Set<string> {
  const seen = new Set<string>();
  if (!rootId) return seen;
  const queue = [rootId];
  while (queue.length) {
    const id = queue.shift()!;
    if (seen.has(id)) continue;
    seen.add(id);
    for (const edge of adj[id] ?? []) {
      if (edge.to_node_id) queue.push(edge.to_node_id);
    }
  }
  return seen;
}

/** Build reverse adjacency so search can expand ancestors. */
function buildReverseAdjacency(adj: AdjacencyMap): Record<string, string[]> {
  const rev: Record<string, string[]> = {};
  for (const [from, list] of Object.entries(adj)) {
    for (const e of list) {
      if (e.to_node_id) (rev[e.to_node_id] ??= []).push(from);
    }
  }
  return rev;
}

// ── Main component ────────────────────────────────────────────────────────────

export function TreeListView({
  treeId, nodes, edges, rootNodeId, canMutate,
  onEditNode, onDeleteNode,
}: TreeListViewProps) {
  const adj = useMemo(() => buildAdjacency(edges), [edges]);
  const nodeMap = useMemo(() => buildNodeMap(nodes), [nodes]);
  const reachable = useMemo(
    () => collectReachable(rootNodeId, adj),
    [rootNodeId, adj],
  );
  const reverseAdj = useMemo(() => buildReverseAdjacency(adj), [adj]);

  const orphans = useMemo(
    () => nodes.filter((n) => !reachable.has(n.id)),
    [nodes, reachable],
  );

  // ── Expansion state: start all-expanded on first mount ────────────────────
  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set(nodes.map((n) => n.id)),
  );
  const onToggle = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  const expandAll = useCallback(
    () => setExpanded(new Set(nodes.map((n) => n.id))),
    [nodes],
  );
  const collapseAll = useCallback(() => setExpanded(new Set()), []);

  // ── Search ────────────────────────────────────────────────────────────────
  const [search, setSearch] = useState('');
  const matchIds = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return null;
    const direct = new Set<string>();
    for (const n of nodes) {
      if (
        n.ref_number.toLowerCase().includes(q) ||
        n.name.toLowerCase().includes(q) ||
        n.node_type.toLowerCase().includes(q)
      ) {
        direct.add(n.id);
      }
    }
    // Expand ancestors.
    const include = new Set(direct);
    const queue = [...direct];
    while (queue.length) {
      const cur = queue.shift()!;
      for (const p of reverseAdj[cur] ?? []) {
        if (!include.has(p)) { include.add(p); queue.push(p); }
      }
    }
    return include;
  }, [search, nodes, reverseAdj]);

  const effectiveExpanded = useMemo(() => {
    if (!matchIds) return expanded;
    return new Set([...expanded, ...matchIds]);
  }, [expanded, matchIds]);

  // ── Add-child popover state ───────────────────────────────────────────────
  const [addChildFor, setAddChildFor] = useState<{ node: IvrNodeRow; anchor: DOMRect } | null>(null);

  const openAddChild = useCallback((node: IvrNodeRow, anchor: DOMRect) => {
    setAddChildFor({ node, anchor });
  }, []);

  // ── Jump-to-node support ──────────────────────────────────────────────────
  // On-complete labels for `custom_node` route to another node in the tree.
  // Clicking the label should scroll that row into view + briefly highlight
  // it. Rows register their DOM ref via a setter, so we can look up the
  // target synchronously. If a node is currently collapsed (ancestor
  // collapsed) we also expand the path to it so it's actually visible.
  const rowRefs = useRef(new Map<string, HTMLDivElement | null>());
  const registerRowRef = useCallback((nodeId: string, el: HTMLDivElement | null) => {
    if (el) rowRefs.current.set(nodeId, el);
    else rowRefs.current.delete(nodeId);
  }, []);
  const [flashNodeId, setFlashNodeId] = useState<string | null>(null);

  const jumpToNode = useCallback((targetId: string) => {
    // Expand ancestors so the target is rendered. Reverse-adj is already
    // computed above for the search feature — walk up from the target.
    setExpanded((prev) => {
      const next = new Set(prev);
      let cursor: string | undefined = targetId;
      const seen = new Set<string>();
      while (cursor && !seen.has(cursor)) {
        seen.add(cursor);
        next.add(cursor);
        cursor = (reverseAdj[cursor] ?? [])[0];
      }
      return next;
    });
    // Defer the scroll + flash to the next frame so the DOM has reflowed
    // after any newly-expanded rows mount.
    requestAnimationFrame(() => {
      const el = rowRefs.current.get(targetId);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setFlashNodeId(targetId);
      setTimeout(() => {
        setFlashNodeId((current) => (current === targetId ? null : current));
      }, 1500);
    });
  }, [reverseAdj]);

  // ── Empty state ───────────────────────────────────────────────────────────
  if (nodes.length === 0) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white px-6 py-12 text-center text-sm text-slate-400">
        <Network className="mx-auto h-6 w-6 text-slate-300 mb-2" />
        No nodes in this tree yet. Use the Nodes tab or the Canvas to create the first node.
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-visible">
      {/* Toolbar + summary strip pinned together — sticky so search /
          expand-all + the 'N matching' counter stay reachable while
          the user scrolls the (now page-height) tree body. */}
      <div className="sticky top-0 z-10">
      <div className="flex items-center gap-3 px-3 py-2 bg-slate-50/95 backdrop-blur supports-[backdrop-filter]:bg-slate-50/70 border-b border-slate-200">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute start-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search tree…"
            aria-label="Search tree"
            className={cn(
              'w-full h-8 rounded-md border border-slate-200 bg-white ps-8 pe-2 text-sm',
              'placeholder:text-slate-400',
              'focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400',
            )}
          />
        </div>

        <div className="flex items-center gap-1 ms-auto">
          <button
            type="button"
            onClick={expandAll}
            className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md border border-slate-200 bg-white text-xs font-medium text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-colors"
          >
            <PlusCircle className="h-3.5 w-3.5" />
            Expand all
          </button>
          <button
            type="button"
            onClick={collapseAll}
            className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md border border-slate-200 bg-white text-xs font-medium text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-colors"
          >
            <MinusCircle className="h-3.5 w-3.5" />
            Collapse all
          </button>
        </div>
      </div>

      {/* Summary strip — second row of the sticky group above. */}
      {rootNodeId && nodeMap[rootNodeId] && (
        <div className="px-3 py-2 text-[11px] text-slate-500 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80 border-b border-slate-100 flex items-center gap-3">
          <ChevronDown className="h-3 w-3 text-slate-400" />
          <span>
            Parent IVR{' '}
            <button
              type="button"
              onClick={() => onEditNode(nodeMap[rootNodeId]!)}
              className="text-indigo-600 font-semibold hover:underline"
            >
              {formatRef(nodeMap[rootNodeId]!.ref_number)}
            </button>
          </span>
          <span className="text-slate-300">·</span>
          <span className="text-slate-400">
            {nodes.length} node{nodes.length === 1 ? '' : 's'}
          </span>
          {matchIds && (
            <>
              <span className="text-slate-300">·</span>
              <span className="text-indigo-600">{matchIds.size} matching</span>
            </>
          )}
        </div>
      )}
      </div>{/* /sticky group */}

      {/* Tree body — nested-cards renderer.
          No inner max-height cap (Sam 2026-05-21): customers with
          hundreds of nodes were stuck scrolling a 70vh frame inside
          the page. Now the body renders at its natural height and
          the whole page scrolls. The toolbar + summary strip above
          are sticky inside this card so search / expand-all stay
          reachable while the user scrolls deep into the tree. */}
      <div>
        {rootNodeId && nodeMap[rootNodeId] ? (
          <StyleNestedCards
            treeId={treeId}
            nodes={nodes}
            edges={edges}
            rootNodeId={rootNodeId}
            canMutate={canMutate}
            adj={adj}
            nodeMap={nodeMap}
            matchIds={matchIds}
            expanded={effectiveExpanded}
            flashNodeId={flashNodeId}
            registerRowRef={registerRowRef}
            orphans={orphans}
            onToggle={onToggle}
            onEditNode={onEditNode}
            onDeleteNode={onDeleteNode}
            onAddChild={openAddChild}
            onJumpToNode={jumpToNode}
          />
        ) : (
          <div className="px-4 py-6 text-sm text-slate-400 italic">
            No root node assigned — pick a root above to see the tree view.
          </div>
        )}
      </div>

      {/* Add-child popover */}
      {addChildFor && (
        <AddChildPopover
          parent={addChildFor.node}
          parentOutgoingEdges={adj[addChildFor.node.id] ?? []}
          treeNodes={nodes}
          treeId={treeId}
          onClose={() => setAddChildFor(null)}
        />
      )}
    </div>
  );
}

